import { observer } from 'mobx-react-lite';

import { Icon } from '@fluentui/react/lib/Icon';
import { PrimaryButton, IButtonStyles } from '@fluentui/react/lib/Button';
import * as React from 'react';
import { MessageType, MessageViewType } from '../store/schema/ResponseMessage';
import { DEFAULT_ACTION_RESPONSE_SNACKBAR_TIMEOUT } from '../constants';
import { getPalette } from 'owa-theme';
import { useWindowEvent } from 'owa-react-hooks/lib/useWindowEvent';

const styles = require('../styles/cardStyles.scss');

export interface ActionResponseProps {
    message: string;
    type: MessageType;
    viewType: MessageViewType;
    autoDismissTimeout?: number;
    onHideActionResponse?: () => void;
    acknowledgeButtonText?: string;
    onAcknowledgeActionResponse?: () => void;
}

const CardWidth = 640;

export default observer(function ActionResponse(props: ActionResponseProps) {
    const containerRef = React.useRef(null);

    let [width, setWidth] = React.useState(CardWidth);

    let hideTimeout: ReturnType<typeof setTimeout> = null;

    React.useEffect(() => {
        if (props.viewType === MessageViewType.AutoDismiss) {
            const timeout = props.autoDismissTimeout || DEFAULT_ACTION_RESPONSE_SNACKBAR_TIMEOUT;
            hideTimeout = setTimeout(() => props.onHideActionResponse(), timeout);
        }
        onResize();

        return () => {
            if (hideTimeout) {
                clearTimeout(hideTimeout);
            }
        };
    }, []);

    const onCloseButtonClicked = () => {
        props.onHideActionResponse();
    };

    const onResize = () => {
        if (containerRef?.current) {
            setWidth(containerRef.current.parentNode.offsetWidth);
        }
    };

    useWindowEvent('resize', onResize);

    let iconName: string;
    if (props.type === MessageType.Success) {
        iconName = 'Completed';
    } else {
        iconName = 'Info';
    }

    const acknowledgeButtonStyles: IButtonStyles = {
        root: {
            backgroundColor: getPalette().neutralLighter,
            borderColor: getPalette().white,
            color: getPalette().neutralPrimary,
            marginLeft: '8px',
            marginRight: '8px',
            paddingLeft: '4px',
            paddingRight: '4px',
        },
        rootHovered: {
            backgroundColor: getPalette().neutralLighterAlt,
            borderColor: getPalette().white,
            color: getPalette().neutralPrimary,
        },
        rootPressed: {
            backgroundColor: getPalette().white,
            borderColor: getPalette().white,
            color: getPalette().neutralPrimary,
        },
        textContainer: {
            overflow: 'hidden',
        },
        label: styles.acknowledgeButtonText,
    };

    return (
        props.message && (
            <div style={{ width }} className={styles.actionResponseContainer} ref={containerRef}>
                <div className={styles.actionResponseWrapper}>
                    <div className={styles.actionResponseStatusIconContainer}>
                        <Icon iconName={iconName} className={styles.actionResponseIcon} />
                    </div>
                    <div className={styles.actionResponseMessageContainer}>
                        <div className={styles.actionResponseMessage}>{props.message}</div>
                    </div>
                    {props.acknowledgeButtonText && props.onAcknowledgeActionResponse ? (
                        <PrimaryButton
                            text={props.acknowledgeButtonText}
                            title={props.acknowledgeButtonText}
                            ariaLabel={props.acknowledgeButtonText}
                            styles={acknowledgeButtonStyles}
                            onClick={props.onAcknowledgeActionResponse}
                        />
                    ) : (
                        ''
                    )}
                    <div
                        className={styles.actionResponseCloseIconContainer}
                        onClick={onCloseButtonClicked}>
                        <Icon iconName={'ChromeClose'} className={styles.actionResponseIcon} />
                    </div>
                </div>
            </div>
        )
    );
});
